/**
 * JSON as text to show, split into the pieces a reader tells apart by colour. The text is
 * exactly `JSON.stringify(value, null, 2)` — the pieces join back into it — so what is
 * copied and what is read are the same characters.
 */

export type JsonTokenKind = 'key' | 'string' | 'number' | 'literal' | 'punctuation' | 'space';

export interface JsonToken {
  readonly kind: JsonTokenKind;
  readonly text: string;
}

export function prettyJson(value: unknown): string {
  return JSON.stringify(value, null, 2) ?? 'undefined';
}

/**
 * A string, then optionally the colon that makes it a key; a number; a literal; a single
 * structural character; or whitespace. Every character of stringify's output is one of
 * these, so the scan never has to skip.
 */
const TOKEN = /("(?:[^"\\]|\\.)*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(true|false|null)|([{}[\],:])|(\s+)/gy;

export function jsonTokens(text: string): ReadonlyArray<JsonToken> {
  const tokens: JsonToken[] = [];
  TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = TOKEN.exec(text)) !== null) {
    const [, string, colon, number, literal, punctuation, space] = match;
    if (string !== undefined) {
      tokens.push({ kind: colon === undefined ? 'string' : 'key', text: string });
      if (colon !== undefined) {
        tokens.push({ kind: 'punctuation', text: colon });
      }
    } else if (number !== undefined) {
      tokens.push({ kind: 'number', text: number });
    } else if (literal !== undefined) {
      tokens.push({ kind: 'literal', text: literal });
    } else if (punctuation !== undefined) {
      tokens.push({ kind: 'punctuation', text: punctuation });
    } else {
      tokens.push({ kind: 'space', text: space });
    }
  }
  // Only stringify's own output is passed in, so this is a bug if it ever fires: show the
  // rest plainly rather than lose it.
  const scanned = tokens.reduce((length, token) => length + token.text.length, 0);
  if (scanned < text.length) {
    tokens.push({ kind: 'string', text: text.slice(scanned) });
  }
  return tokens;
}
